// The panel's own gate on the address, deliberately as strict as the backend's
// `@IsEmail()`. `class-validator` delegates to `validator.js` `isEmail` with
// default options, so what follows ports that one path: no display name, no IP
// literal domain, UTF-8 local parts allowed, a real TLD required. Keeping the
// two in step is what stops a mistyped address from reaching the backend and
// coming back as the ambiguous 400 the copy reads as a rate limit.
const MAX_ADDRESS_LENGTH = 254
const MAX_LOCAL_PART_BYTES = 64
const MAX_DOMAIN_BYTES = 254
const MAX_DOMAIN_LABEL_LENGTH = 63

// The character ranges are spelled as code points rather than as literals so
// no invisible character (U+00A0 among them) can hide inside a pattern.
const range = (from: number, to: number): string =>
  `${String.fromCharCode(from)}-${String.fromCharCode(to)}`

// What upstream counts as a "UTF-8 letter": everything above Latin-1
// punctuation, minus the surrogate block and the specials at the top.
const UTF8_LETTERS = range(0xf900, 0xfdcf) + range(0xfdf0, 0xffef)
const UTF8_LOCAL_PART = range(0x00a1, 0xd7ff) + UTF8_LETTERS
// The quoted form reaches one code point lower — it admits U+00A0 too.
const UTF8_QUOTED = range(0x00a0, 0xd7ff) + UTF8_LETTERS

const LOCAL_PART_RE = new RegExp(
  `^[a-z\\d!#$%&'*+\\-/=?^_\`{|}~${UTF8_LOCAL_PART}]+$`,
  "i"
)
// A quoted local part ("john doe"@example.com) may carry characters the plain
// form may not — spaces, and anything escaped with a backslash.
const QUOTED_LOCAL_PART_RE = new RegExp(
  `^([\\s\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x7f\\x21\\x23-\\x5b\\x5d-\\x7e${UTF8_QUOTED}]` +
    `|(\\\\[\\x01-\\x09\\x0b\\x0c\\x0d-\\x7f${UTF8_QUOTED}]))*$`,
  "i"
)
// No underscore in the set: `allow_underscores` is off by default upstream.
const DOMAIN_LABEL_RE = new RegExp(`^[a-z\\d${range(0x00a1, 0xffff)}-]+$`, "i")
const FULL_WIDTH_RE = new RegExp(`[${range(0xff01, 0xff5e)}]`)
const EDGE_HYPHEN_RE = /^-|-$/
// Two letters minimum, or a punycode `xn--…` label. This is the clause that
// refuses `a@b.c` and `me@mail.x`.
const TLD_RE = new RegExp(
  `^([a-z${range(0x00a1, 0x00a8)}${range(0x00aa, 0xd7ff)}${UTF8_LETTERS}]{2,}` +
    `|xn[a-z\\d-]{2,})$`,
  "i"
)

const byteLength = (value: string): number =>
  new TextEncoder().encode(value).length

const isValidDomain = (domain: string): boolean => {
  const labels = domain.split(".")

  if (labels.length < 2 || !TLD_RE.test(labels[labels.length - 1])) {
    return false
  }

  return labels.every(
    (label) =>
      label.length <= MAX_DOMAIN_LABEL_LENGTH &&
      DOMAIN_LABEL_RE.test(label) &&
      !FULL_WIDTH_RE.test(label) &&
      !EDGE_HYPHEN_RE.test(label)
  )
}

const isValidLocalPart = (localPart: string): boolean => {
  if (localPart.startsWith('"') && localPart.endsWith('"')) {
    return QUOTED_LOCAL_PART_RE.test(localPart.slice(1, -1))
  }

  // Dots split the local part into pieces and none of them may be empty —
  // that is what refuses a leading, trailing or doubled dot.
  return localPart.split(".").every((piece) => LOCAL_PART_RE.test(piece))
}

export const isValidEmail = (value: string): boolean => {
  if (value.length > MAX_ADDRESS_LENGTH) {
    return false
  }

  const segments = value.split("@")
  const domain = segments.pop() ?? ""
  const localPart = segments.join("@")

  if (
    byteLength(localPart) > MAX_LOCAL_PART_BYTES ||
    byteLength(domain) > MAX_DOMAIN_BYTES
  ) {
    return false
  }

  return isValidDomain(domain) && isValidLocalPart(localPart)
}

export const AUTH_ERROR_MESSAGE_KEYS = {
  emailNotRegistered: "auth.errors.emailNotRegistered",
  emailAlreadyRegistered: "auth.errors.emailAlreadyRegistered",
  invalidCode: "auth.errors.invalidCode",
  rateLimited: "auth.errors.rateLimited",
  mailUnavailable: "auth.errors.mailUnavailable",
  generic: "errors.generic"
} as const

// Statuses the `/auth/email/*` endpoints answer with:
//   404 — no account for this address (login intent)
//   409 — the address is already taken (create intent)
//   401 — the code is wrong or expired
//   400 — too many codes requested for this address
//   429 — too many calls from this device (per-IP rate limit guard)
//   503 — the mail service refused to deliver the code
// The backend sends no Retry-After, so the copy can only say "later".
export const authErrorMessageKey = (status?: number): string => {
  switch (status) {
    case 404:
      return AUTH_ERROR_MESSAGE_KEYS.emailNotRegistered
    case 409:
      return AUTH_ERROR_MESSAGE_KEYS.emailAlreadyRegistered
    case 401:
      return AUTH_ERROR_MESSAGE_KEYS.invalidCode
    case 400:
    case 429:
      return AUTH_ERROR_MESSAGE_KEYS.rateLimited
    case 503:
      return AUTH_ERROR_MESSAGE_KEYS.mailUnavailable
    default:
      return AUTH_ERROR_MESSAGE_KEYS.generic
  }
}
